import { useRouter } from "next/router"
import { useQuery } from "@tanstack/react-query"
import { graphql } from "@/graphql"
import { useGraphQLClient, useSession } from "@/lib/blueprint-auth/client"
import { externalRoutesPaths } from "@/routes/external"

const TYPEFORM_ERROR_PATHS = ["/souscription", "/optimisation-compteur"]

const BASE_TYPEFORM_URL = externalRoutesPaths.octopusTypeform.resolve({
    pathParams: { formId: "LeoKQ2s3" },
})

const ViewerQuery = graphql(`
    query Viewer {
        viewer {
            accounts {
                ... on AccountType {
                    number
                }
            }
        }
    }
`)

type UseTypeformErrorProps = {
    errorMessage: string
    typeformFallbackURL?: string
}

export function useTypeformError({ errorMessage, typeformFallbackURL }: UseTypeformErrorProps) {
    const router = useRouter()
    const { data: session } = useSession()
    const graphQLClient = useGraphQLClient()

    const { data: user } = useQuery({
        queryKey: ["useTypeformError"],
        queryFn: () => graphQLClient.request(ViewerQuery),
        enabled: !!session?.isAuthenticated && session?.authMethod !== "scoped",
        select: (data) => data.viewer,
    })
    const currentAccount = user?.accounts?.[0] ?? null

    const isTypeformError = TYPEFORM_ERROR_PATHS.find((path) => router.pathname.includes(path))
    const { electricityMeterPointId, gasMeterPointId, propertyId } = router.query

    const hiddenParams = new URLSearchParams({
        visited_url: router.pathname,
        error: errorMessage,
        ...(currentAccount?.number ? { account_id: currentAccount?.number } : {}),
        ...(electricityMeterPointId ? { electricity_meter: String(electricityMeterPointId) } : {}),
        ...(gasMeterPointId ? { gas_meter: String(gasMeterPointId) } : {}),
        ...(propertyId ? { property: String(propertyId) } : {}),
    })

    const typeformUrl = `${typeformFallbackURL ?? BASE_TYPEFORM_URL}#${hiddenParams.toString()}`

    function redirectToTypeform() {
        return router.push(typeformUrl)
    }

    return { isTypeformError, typeformUrl, redirectToTypeform }
}
